import React, { Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { CSpinner, useColorModes } from '@coreui/react'
import './scss/style.scss'
import { AUTH_CHANGE_EVENT, isAuthenticated } from './auth'
import { initInstallPromptCapture } from './pwaInstallPrompt'

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'))
const ForgotPassword = React.lazy(() => import('./views/pages/login/ForgotPassword'))
const ResetPassword = React.lazy(() => import('./views/pages/login/ResetPassword'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const CrewSignup = React.lazy(() => import('./views/pages/crewSignup/CrewSignup'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))

const App = () => {
  const { isColorModeSet, setColorMode } = useColorModes('digitalhugs-crm-theme')
  const storedTheme = useSelector((state) => state.theme)
  const [authenticated, setAuthenticated] = useState(isAuthenticated())

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1])
    const theme = urlParams.get('theme') && urlParams.get('theme').match(/^[A-Za-z0-9\s]+/)[0]
    if (theme) {
      setColorMode(theme)
    }

    if (isColorModeSet()) {
      return
    }

    setColorMode(storedTheme)
  }, [])

  useEffect(() => {
    initInstallPromptCapture()

    const syncAuthState = () => {
      setAuthenticated(isAuthenticated())
    }

    window.addEventListener(AUTH_CHANGE_EVENT, syncAuthState)
    window.addEventListener('storage', syncAuthState)

    // If the browser restores this page from bfcache (e.g. pressing Back after
    // logout, or on a shared device), no JS re-runs by default — re-check auth so a
    // stale authenticated screen doesn't stay visible with a logged-out session.
    const handlePageShow = (event) => {
      if (event.persisted) {
        syncAuthState()
      }
    }
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, syncAuthState)
      window.removeEventListener('storage', syncAuthState)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="pt-3 text-center">
            <CSpinner color="primary" variant="grow" />
          </div>
        }
      >
        <Routes>
          <Route
            exact
            path="/login"
            name="Login Page"
            element={authenticated ? <Navigate to="/dashboard" replace /> : <Login />}
          />
          <Route exact path="/forgot-password" name="Forgot Password" element={<ForgotPassword />} />
          <Route exact path="/reset-password" name="Reset Password" element={<ResetPassword />} />
          <Route exact path="/register" name="Register Page" element={<Register />} />
          <Route exact path="/crew-signup" name="Crew Signup" element={<CrewSignup />} />
          <Route exact path="/404" name="Page 404" element={<Page404 />} />
          <Route exact path="/500" name="Page 500" element={<Page500 />} />
          <Route
            path="*"
            name="Home"
            element={authenticated ? <DefaultLayout /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
